import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useState, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Box } from '../Box/index.js';
import { Icon } from '../Icon/index.js';
import { useInputGroupContext } from '../InputGroup/index.js';
import { IconButton } from '../IconButton/index.js';
import { StyledDatePicker } from './datePickerStyles.js';

/**
 * DatePicker is an input field that enables users to enter a date and/or time.
 *
 * DatePicker is abstracted from [react-datepicker](https://reactdatepicker.com/), providing us with interactive date and time picking capabilities.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/datepicker#usage)
 * - [Props](https://blockparty.endpoint.com/components/datepicker#props)
 * <br />
 *
 */
const DatePicker = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    dataTestId = 'datepicker',
    isDisabled,
    isInvalid,
    isRequired,
    isOverDue = false,
    iconType,
    width = '100%',
    selectsRange = false,
    isClearable = false,
    selected,
    onClearButtonClick,
    ...props
  } = _ref;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const datePickerRef = useRef(null);
  const formControl = useInputGroupContext();
  const _isDisabled = isDisabled || formControl.isDisabled;
  const _isInvalid = isInvalid || formControl.isInvalid;
  const _isRequired = isRequired || formControl.isRequired;
  const _id = props?.id || formControl.groupId;
  // triggers calendar on icon click
  const handleIconClick = () => {
    const datepickerElement = datePickerRef.current;
    // @ts-ignore
    datepickerElement.setFocus(true);
  };
  return /*#__PURE__*/React__default.createElement(StyledDatePicker, {
    ref: forwardedRef,
    "data-test-id": dataTestId,
    iconType: iconType,
    isInvalid: _isInvalid,
    isOverDue: isOverDue,
    position: "relative",
    width: width
  }, /*#__PURE__*/React__default.createElement(ReactDatePicker
  // @ts-ignore
  , _extends({
    ref: datePickerRef,
    dateFormat: props.showTimeSelect ? 'h:mm aa' : undefined,
    disabled: _isDisabled,
    id: _id,
    required: _isRequired,
    selected: selected,
    selectsRange: selectsRange,
    timeFormat: props.showTimeSelect ? 'h:mm aa' : undefined,
    onCalendarClose: () => {
      setIsCalendarOpen(false);
    },
    onCalendarOpen: () => {
      setIsCalendarOpen(true);
    }
  }, props)), isClearable && selected && !_isDisabled && /*#__PURE__*/React__default.createElement(IconButton, {
    _focus: {
      backgroundColor: 'transparent'
    },
    _hover: {
      backgroundColor: 'transparent'
    },
    _selected: {
      backgroundColor: 'transparent'
    },
    backgroundColor: "transparent",
    color: "carbon500",
    dataTestId: "date-picker-clear-button",
    label: "clear datepicker",
    my: "space30",
    name: "Close",
    padding: "space0",
    position: "absolute",
    right: "38px",
    top: "0px",
    variant: "unstyled",
    onClick: onClearButtonClick
  }), iconType && /*#__PURE__*/React__default.createElement(DatePickerIcon, {
    iconType: iconType,
    isCalendarOpen: isCalendarOpen,
    isDisabled: _isDisabled,
    onClick: handleIconClick
  }));
});
const DatePickerIcon = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    isCalendarOpen,
    isDisabled,
    iconType,
    onClick
  } = _ref2;
  return /*#__PURE__*/React__default.createElement(Box, {
    borderLeft: "1px solid",
    borderLeftColor: "carbon300",
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    "data-test-id": "datepicker-icon",
    my: "space30",
    position: "absolute",
    px: "space20",
    right: "1px",
    top: "0px",
    onClick: onClick
  }, /*#__PURE__*/React__default.createElement(Icon, {
    ref: forwardedRef,
    "aria-hidden": "true",
    color: isDisabled ? 'carbon200' : isCalendarOpen ? 'carbon600' : 'carbon500',
    name: iconType === 'calendar' ? 'CalendarDateRange' : 'Clock',
    outline: "none",
    size: "large",
    tabIndex: -1
  }));
});

export { DatePicker };
